import { call, fork, getContext, put, select, take, takeEvery } from 'redux-saga/effects';
import { DecodedRouteParams, Route } from 'config/routes';
import { waitForUserPreferences, waitForUserResolve } from 'store/sagas/routing/handlers/utils';
import { navigateTo } from 'store/state/router/actions';
import { isConnectedUserSelector, searchDocEntriesSelector, userHasAcceptedInvestorsTermsSelector } from 'store/state/domainData/selectors';
import { setInvestorsLoginStep, setToastMessage, setInvestorsFeedbackModalStateAction, setInvestorsRecentSearchIdAction, setInvestorsCompareModalAction } from 'store/state/app/actions';
import { AuthenticationModalType } from 'components/authentication/types';
import { queryData } from 'store/sagas/apiService';
import { IInvestorPreferences, INeighbourhoodInvestorSummary, LoadType, MutationType } from 'store/sagas/apiService/types';
import { RootAction } from 'store/state';
import { investorCalculatedBudgetDataSelector, investorNeighbourhoodSummarySelector, investorPreferencesDataSelector, investorRecentSearchesSelector } from 'store/state/domainData/selectors/investors';
import { find, isNil, get } from 'lodash';
import { getRouteParams, prevRouteSelector, routeNameSelector, routeSelector } from 'store/state/selectors/router';
import { resetDomainData } from 'store/state/domainData/actions';
import { MUTATE, SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { DealType, IAutocompleteNeighbourhood, InsightDescriptor, ISearchPoiVariables } from 'utils/entities';
import { UserTokenStore } from 'helpers/userToken';
import { SET_DOMAIN_DATA } from 'store/state/domainData/types';
import { isNeighbourhoodId } from 'helpers';
import { SET_INVESTORS_COMPARE_MODAL_OPEN, SET_INVESTORS_RECENT_SEARCH_ID } from 'store/state/app/types';
import { mutate } from 'store/state/mutationsResponse/actions';
import { GET_MATCHED_LISTINGS_COUNT } from 'store/state/investors/types';


const investorsWizardSummaryUpdatePattern = (action: RootAction) => (
  action.type === MUTATE && action.mutationType === MutationType.UpdateInvestorPreferences && action.meta.variables.input.hasProperty !== null
);

const investorsFeedbackPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.InvestorsFeedback
);

const toggleFavoriteNeighbourhoodPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && [
    MutationType.AddInvestorFavoriteNeighbourhood,
    MutationType.RemoveInvestorFavoriteNeighbourhood,
  ].includes(action.mutationType)
);

const investorsWizardSummaryResponsePattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.UpdateInvestorPreferences
);

const searchSavedPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.SaveInvestorRecentSearches && (action.meta.response.data as any).saveInvestorRecentSearches.success
);

function* investorsHomePageHandler() {
  yield fork(queryData, { loadType: LoadType.InvestorCalculatedInvestorBudget, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorMarketStats, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorNationalSalesHistory, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorPopularListingsOnePerCity, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorSuggestedListings, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.FavoriteNeighborhoods, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorBulletinsWithPriceDrop, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorHighRentYieldNeighborhoods, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorNeighborhoodsWithGrowthPotential, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorSuggestedNeighborhoods, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorHighRentYieldUserPreferencesNeighborhoods, meta: { variables: { usePreferences: true } } });
  yield fork(queryData, { loadType: LoadType.InvestorUserPreferencesNeighborhoodsWithGrowthPotential, meta: { variables: { usePreferences: true } } });
}

function* investorsLobbyPageHandler() {
  yield fork(queryData, { loadType: LoadType.InvestorMarketStats, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorPopularListingsOnePerCity, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorBulletinsWithPriceDrop, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorHighRentYieldNeighborhoods, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorNeighborhoodsWithGrowthPotential, meta: { variables: {} } });
}

function* toggleFavoriteNeighbourhoodWorker() {
  yield fork(queryData, { loadType: LoadType.FavoriteNeighborhoods, meta: { variables: { timestamp: Date.now() } } });
}

export const searchDocPattern = (action: RootAction) => {
  if (action.type === SET_DOMAIN_DATA) {
    return action.loadType === LoadType.SearchDoc && Boolean(action.payload.data);
  }
  return false;
};

function* investorsSearchPageWorker() {
  const tokenStore: UserTokenStore = yield getContext('tokenStore');
  const userToken = tokenStore.get();

  const routeParams: DecodedRouteParams = yield select(getRouteParams);

  yield fork(queryData, { loadType: LoadType.FavoriteNeighborhoods, meta: { variables: {} } });
  if (routeParams.term && routeParams.term.length === 1 && isNeighbourhoodId(routeParams.term[0])) {
    const docId = routeParams.term[0];

    const summaryDate: INeighbourhoodInvestorSummary = yield select(investorNeighbourhoodSummarySelector);
    if (summaryDate && summaryDate.neighborhoodDoc.docId !== docId) {
      yield put(resetDomainData({ loadType: LoadType.NeighbourhoodInvestorSummary }));
    }

    yield fork(queryData, {
      loadType: LoadType.NeighbourhoodInvestorSummary,
      meta: {
        variables: {
          userToken1: userToken,
          userToken2: userToken,
          neighborhoodDocId: docId,
          query: {
            limit: 30,
            offset: 0,
            publishStatus: [ 'PUBLISHED' ],
            neighborhoodDocId: docId,
          },
        },
      },
    });
  }
}

export function* investorsNeighbourhoodPageHandler() {
  const routeParams: DecodedRouteParams = yield select(getRouteParams);
  const docId = routeParams.id;
  const comparedDocId = routeParams.compareId;

  const tokenStore: UserTokenStore = yield getContext('tokenStore');
  const userToken = tokenStore.get();

  yield fork(queryData, { loadType: LoadType.FavoriteNeighborhoods, meta: { variables: {} } });
  yield fork(queryData, { loadType: LoadType.InvestorSuggestedNeighborhoods, meta: { variables: {} } });

  // the page will show a spinner until this query is resolved
  yield fork(queryData, {
    loadType: LoadType.NeighbourhoodInvestorSummary,
    meta: {
      variables: {
        userToken1: userToken,
        userToken2: userToken,
        neighborhoodDocId: docId,
        query: {
          limit: 30,
          offset: 0,
          publishStatus: [ 'PUBLISHED' ],
          neighborhoodDocId: docId,
        },
      },
    },
  });

  if (comparedDocId) {
    yield fork(queryData, {
      loadType: LoadType.ComparedNeighbourhoodInvestorSummary,
      meta: {
        variables: {
          userToken1: userToken,
          userToken2: userToken,
          neighborhoodDocId: comparedDocId,
          query: {
            limit: 30,
            offset: 0,
            publishStatus: [ 'PUBLISHED' ],
            neighborhoodDocId: comparedDocId,
          },
        },
      },
    });
  }

  yield fork(queryData, {
    loadType: LoadType.NeighbourhoodInvestorPriceTrendsInsight,
    meta: {
      variables: {
        docId,
        insights: [ InsightDescriptor.PriceTrendsNew ],
        withGeoData: false,
      },
    },
  });

  if (comparedDocId) {
    yield fork(queryData, {
      loadType: LoadType.ComparedNeighbourhoodInvestorPriceTrendsInsight,
      meta: {
        variables: {
          docId: comparedDocId,
          insights: [ InsightDescriptor.PriceTrendsNew ],
          withGeoData: false,
        },
      },
    });
  }

  // fetch matched prefs listings
  yield call(queryData, { loadType: LoadType.InvestorCalculatedInvestorBudget, meta: { variables: {} } });
  const investorMaxBudget: { totalBudget: number } = yield select(investorCalculatedBudgetDataSelector);

  const sharedListingSearchVariables: ISearchPoiVariables = {
    dealType: DealType.Buy,
    limit: 5,
    tileRanges: null,
    poiTypes: [ 'bulletin' ],
    priceRange: investorMaxBudget && investorMaxBudget.totalBudget ? [ null, investorMaxBudget.totalBudget ] : undefined,
  };

  yield fork(queryData, {
    loadType: LoadType.ListingPostLeadCarouselSearch,
    meta: { variables: { locationDocId: docId, ...sharedListingSearchVariables } },
  });

  if (comparedDocId) {
    yield fork(queryData, {
      loadType: LoadType.AdditionalCarouselSearch,
      meta: { variables: { locationDocId: comparedDocId, ...sharedListingSearchVariables } },
    });
  }
}

export function* investorsPageHandler() {
  const prevRoute = yield select(prevRouteSelector);
  const routeName = yield select(routeNameSelector);

  if (prevRoute) {
    yield put(resetDomainData({ loadType: LoadType.FavoriteNeighborhoods }));
    yield put(resetDomainData({ loadType: LoadType.InvestorCalculatedInvestorBudget }));
    yield put(resetDomainData({ loadType: LoadType.InvestorMarketStats }));
    yield put(resetDomainData({ loadType: LoadType.InvestorNationalSalesHistory }));
    yield put(resetDomainData({ loadType: LoadType.InvestorPopularListingsOnePerCity }));
    yield put(resetDomainData({ loadType: LoadType.InvestorSuggestedListings }));
    yield put(resetDomainData({ loadType: LoadType.InvestorBulletinsWithPriceDrop }));
    yield put(resetDomainData({ loadType: LoadType.InvestorHighRentYieldNeighborhoods }));
    yield put(resetDomainData({ loadType: LoadType.InvestorNeighborhoodsWithGrowthPotential }));
    yield put(resetDomainData({ loadType: LoadType.InvestorSuggestedNeighborhoods }));
    yield put(resetDomainData({ loadType: LoadType.InvestorHighRentYieldUserPreferencesNeighborhoods }));
    yield put(resetDomainData({ loadType: LoadType.InvestorUserPreferencesNeighborhoodsWithGrowthPotential }));
    if (routeName !== Route.InvestorsSearchPage) {
      yield put(resetDomainData({ loadType: LoadType.NeighbourhoodInvestorSummary }));
    }
    yield put(resetDomainData({ loadType: LoadType.NeighbourhoodInvestorPriceTrendsInsight }));
    yield put(resetDomainData({ loadType: LoadType.ListingPostLeadCarouselSearch }));
    yield put(resetDomainData({ loadType: LoadType.ComparedNeighbourhoodInvestorSummary }));
    yield put(resetDomainData({ loadType: LoadType.ComparedNeighbourhoodInvestorPriceTrendsInsight }));
    yield put(resetDomainData({ loadType: LoadType.AdditionalCarouselSearch }));
  }

  yield call(waitForUserResolve);

  const { name } = yield select(routeSelector);

  const hasAcceptedInvestorsTerms: boolean = yield select(userHasAcceptedInvestorsTermsSelector);
  const isConnectedUser: boolean = yield select(isConnectedUserSelector);

  if (!isConnectedUser && name !== Route.InvestorsLoginPage) {
    yield put(setInvestorsLoginStep({ type: null, authSource: null, authSourceVariables: null }, { isUserInitiated: false }));
    yield put(navigateTo(Route.InvestorsLoginPage));
  }
  else if (isConnectedUser) {
    if (!hasAcceptedInvestorsTerms) {
      yield put(setInvestorsLoginStep({ type: AuthenticationModalType.InvestorsLoginTerms }, { isUserInitiated: false }));
      yield put(navigateTo(Route.InvestorsLoginPage));
    }
    else {
      yield put(resetDomainData({ loadType: LoadType.InvestorPreferences }));
      yield call(queryData, { loadType: LoadType.InvestorPreferences, meta: { variables: {} } });

      yield call(waitForUserPreferences);

      const data: IInvestorPreferences = yield select(investorPreferencesDataSelector);

      const tokenStore: UserTokenStore = yield getContext('tokenStore');
      const userToken = tokenStore.get();
      yield fork(queryData, { loadType: LoadType.InvestorRecentSearches, meta: { variables: { userToken } } });

      const hasPreferences = !isNil(data)
        && !isNil(data.budget)
        && !isNil(data.goal)
        && !isNil(data.hasProperty)
        && !isNil(data.places);

      switch (name) {
        case Route.InvestorsNeighbourhoodPage:
          yield fork(investorsNeighbourhoodPageHandler);
          break;
        case Route.InvestorsHomePage:
          if (hasPreferences) yield fork(investorsHomePageHandler);
          else yield put(navigateTo(Route.InvestorsLobbyPage));
          break;
        case Route.InvestorsLobbyPage:
          if (hasPreferences) yield put(navigateTo(Route.InvestorsHomePage));
          else yield fork(investorsLobbyPageHandler);
          break;
        case Route.InvestorsLoginPage:
          if (hasPreferences) yield put(navigateTo(Route.InvestorsHomePage));
          else yield put(navigateTo(Route.InvestorsLobbyPage));
          break;
        case Route.InvestorsFavoriteNeighourhoodPage:
          yield fork(toggleFavoriteNeighbourhoodWorker);
          break;
        case Route.InvestorsSearchPage:
          yield fork(investorsSearchPageWorker);
          break;
        case Route.InvestorsProfilePage:
          yield fork(queryData, { loadType: LoadType.FavoriteNeighborhoods, meta: { variables: {} } });
          break;
      }
    }
  }
}

function* investorRecentSearchWatcher(action: ReturnType<typeof setInvestorsRecentSearchIdAction>) {
  if (action.payload) {
    const recentSearches = yield select(investorRecentSearchesSelector);
    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();

    if (recentSearches && recentSearches.length && find(recentSearches, search => search.doc.docId === action.payload)) return;

    yield put(mutate({
      mutationType: MutationType.SaveInvestorRecentSearches,
      meta: {
        variables: {
          userToken,
          docIds: [ action.payload ],
        },
      },
    }));
  }
}

function* investorLoadRecentSearches() {
  const tokenStore: UserTokenStore = yield getContext('tokenStore');
  const userToken = tokenStore.get();

  yield put(resetDomainData({ loadType: LoadType.InvestorRecentSearches }));
  yield fork(queryData, { loadType: LoadType.InvestorRecentSearches, meta: { variables: { userToken } } });
}

function* investorWizardSummaryWatcher() {
  yield put(resetDomainData({ loadType: LoadType.InvestorCalculatedInvestorBudget }));
  yield take(investorsWizardSummaryResponsePattern);
  yield fork(queryData, { loadType: LoadType.InvestorCalculatedInvestorBudget, meta: { variables: {} } });
}

function* investorProfilePreferencesWatcher() {
  const routeName = yield select(routeNameSelector);

  if (routeName === Route.InvestorsProfilePage) {
    yield fork(queryData, { loadType: LoadType.InvestorPreferences, meta: { variables: { uiNonCache: Date.now().toString() } } });
    yield put(setToastMessage({ term: 'changes.saved', type: 'success-dark' }));
  }
}

function* investorFeedbackWatcher(val: any) {
  const isSuccess = get(val, 'meta.response.data.saveInvestorUserFeedback.success');
  if (isSuccess) {
    yield put(setInvestorsFeedbackModalStateAction({ isModalOpen: false, isPostFeedbackModalOpen: true }));
  }
  else {
    yield put(setToastMessage({ term: 'investors.disclaimer.error.copy', type: 'error-floating', autoHideTimeout: 6000 }));
  }
}

function* matchedListingCountWatcher() {
  try {
    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();

    const items: IAutocompleteNeighbourhood[] = yield select(searchDocEntriesSelector);
    const investorPreferences: IInvestorPreferences = yield select(investorPreferencesDataSelector);

    if (items && items.length > 2) {
      yield put(resetDomainData({ loadType: LoadType.InvestorMatchedListingsCount }));
      yield call(queryData, {
        loadType: LoadType.InvestorMatchedListingsCount,
        meta: {
          variables: {
            userToken,
            input: {
              budget: investorPreferences && investorPreferences.budget || undefined,
              neighborhoodDocIds: items.map(el => el.docId),
            },
          },
        },
      });
    }
  }
  catch (e) {
    const logger = yield getContext('logger');
    logger.error('Matched listing was not successful', e);
  }
}

function* compareModalWatcher(action: ReturnType<typeof setInvestorsCompareModalAction>) {
  if (action && action.payload && action.payload.id) {
    const tokenStore: UserTokenStore = yield getContext('tokenStore');
    const userToken = tokenStore.get();
    const similarData: INeighbourhoodInvestorSummary = yield select(investorNeighbourhoodSummarySelector);
    if (similarData && similarData.similarNeighborhoods.length) {
      yield put(resetDomainData({ loadType: LoadType.InvestorSimilarNeighbourhoods }));
    }

    yield fork(queryData, {
      loadType: LoadType.InvestorSimilarNeighbourhoods,
      meta: {
        variables: {
          userToken,
          neighborhoodDocId: action.payload.id,
        },
      },
    });
  }
}

export function* investorsLobbyMaybeRedirectToHomePage() {
  yield call(waitForUserResolve);

  const hasAcceptedInvestorsTerms: boolean = yield select(userHasAcceptedInvestorsTermsSelector);

  if (!hasAcceptedInvestorsTerms) {
    yield put(setInvestorsLoginStep({ type: AuthenticationModalType.InvestorsLoginTerms }, { isUserInitiated: false }));
    yield put(navigateTo(Route.InvestorsLoginPage));
  }
  else {
    yield put(resetDomainData({ loadType: LoadType.InvestorPreferences }));
    yield call(queryData, { loadType: LoadType.InvestorPreferences, meta: { variables: {} } });

    yield call(waitForUserPreferences);

    const data: IInvestorPreferences = yield select(investorPreferencesDataSelector);

    const hasPreferences = !isNil(data)
      && !isNil(data.budget)
      && !isNil(data.goal)
      && !isNil(data.hasProperty)
      && !isNil(data.places);

    if (hasPreferences) yield put(navigateTo(Route.InvestorsHomePage));
    else yield put(navigateTo(Route.InvestorsLobbyPage));
  }
}

export function* investorsWatcher() {
  yield takeEvery(toggleFavoriteNeighbourhoodPattern, toggleFavoriteNeighbourhoodWorker);
  yield takeEvery(investorsWizardSummaryUpdatePattern, investorWizardSummaryWatcher);
  yield takeEvery(SET_INVESTORS_RECENT_SEARCH_ID, investorRecentSearchWatcher);
  yield takeEvery(searchSavedPattern, investorLoadRecentSearches);
  yield takeEvery(investorsWizardSummaryResponsePattern, investorProfilePreferencesWatcher);
  yield takeEvery(investorsFeedbackPattern, investorFeedbackWatcher);
  yield takeEvery(GET_MATCHED_LISTINGS_COUNT, matchedListingCountWatcher);
  yield takeEvery(SET_INVESTORS_COMPARE_MODAL_OPEN, compareModalWatcher);
}
